import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { switchStore as switchStoreApi } from '@/lib/api/auth.js';
import {
  createStore,
  listStores,
  updateStoreById,
  type StoreRow,
} from '@/lib/api/stores.js';
import { SlideOver } from '@/components/SlideOver.js';
import { useAuthStore } from '@/stores/auth.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { SETTINGS_TIMEZONES } from '@/pages/settings/settings-utils.js';

type StoreListRow = StoreRow & {
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  timezone?: string | null;
  taxRate?: string | number | null;
  ticketNumberPrefix?: string | null;
};

type LocationForm = {
  name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
  ticketNumberPrefix: string;
  taxRate: string;
};

const emptyForm = (): LocationForm => ({
  name: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  timezone: 'America/Detroit',
  ticketNumberPrefix: 'TKT',
  taxRate: '0',
});

export function LocationsSettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const switchStore = useAuthStore((s) => s.switchStore);
  const [slideOpen, setSlideOpen] = useState(false);
  const [editing, setEditing] = useState<StoreListRow | null>(null);
  const [form, setForm] = useState<LocationForm>(emptyForm());

  const storesQuery = useQuery({
    queryKey: ['stores-list'],
    queryFn: listStores,
  });

  const stores = (storesQuery.data ?? []) as StoreListRow[];

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        timezone: form.timezone,
        ticketNumberPrefix: form.ticketNumberPrefix.trim() || 'TKT',
        taxRate: Number(form.taxRate) / 100,
      };
      if (editing) return updateStoreById(editing.id, body);
      return createStore(body);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['stores-list'] });
      toastSuccess(editing ? 'Location updated' : `${form.name} created!`);
      setSlideOpen(false);
      setEditing(null);
      if (!editing && created?.id) {
        setForm(emptyForm());
      }
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const switchMut = useMutation({
    mutationFn: (storeId: string) => switchStoreApi(storeId),
    onSuccess: (data) => {
      switchStore(data.user, data.token);
      window.location.reload();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deactivateMut = useMutation({
    mutationFn: (storeId: string) => updateStoreById(storeId, { active: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores-list'] });
      toastSuccess('Location deactivated');
      setSlideOpen(false);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function openAdd() {
    setEditing(null);
    setForm(emptyForm());
    setSlideOpen(true);
  }

  function openEdit(store: StoreListRow) {
    setEditing(store);
    setForm({
      name: store.name ?? '',
      phone: store.phone ?? '',
      email: (store as { email?: string }).email ?? '',
      address: store.address ?? '',
      city: store.city ?? '',
      state: store.state ?? '',
      zip: store.zip ?? '',
      timezone: store.timezone ?? 'America/Detroit',
      ticketNumberPrefix: store.ticketNumberPrefix ?? 'TKT',
      taxRate: String(Number(store.taxRate ?? 0) * 100),
    });
    setSlideOpen(true);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    saveMut.mutate();
  }

  if (user?.role !== 'tenant_admin' && user?.role !== 'platform_admin') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600 shadow-sm">
        Locations management requires tenant admin access.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-950">Locations</h2>
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
          onClick={openAdd}
        >
          + Add Location
        </button>
      </div>

      <div className="space-y-3">
        {stores.map((store) => (
          <article
            key={store.id}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-bold text-slate-950">
                  {store.name}{' '}
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                    {store.active === false ? 'Inactive' : 'Active'}
                  </span>
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  {[store.address, store.city, store.state, store.zip].filter(Boolean).join(', ')}
                </p>
                {store.phone ? <p className="text-sm text-slate-600">{store.phone}</p> : null}
                <p className="mt-1 text-xs text-slate-500">
                  Prefix: {store.ticketNumberPrefix ?? 'TKT'} · Tax:{' '}
                  {(Number(store.taxRate ?? 0) * 100).toFixed(2)}%
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold"
                  onClick={() => openEdit(store)}
                >
                  Edit
                </button>
                {store.id !== user?.storeId ? (
                  <button
                    type="button"
                    className="rounded-lg border border-primary px-3 py-1.5 text-sm font-semibold text-primary disabled:opacity-50"
                    disabled={switchMut.isPending}
                    onClick={() => switchMut.mutate(store.id)}
                  >
                    Switch to this store
                  </button>
                ) : (
                  <span className="rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary">
                    Current store
                  </span>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      <SlideOver
        title={editing ? 'Edit Location' : 'Add New Location'}
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
      >
        <form className="space-y-4" onSubmit={submit}>
          <label className="block">
            <span className="text-sm font-medium">Store Name *</span>
            <input
              required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Phone *</span>
            <input
              required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Email</span>
            <input
              type="email"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Address *</span>
            <input
              required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium">City *</span>
              <input
                required
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">State *</span>
              <input
                required
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium">ZIP *</span>
            <input
              required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={form.zip}
              onChange={(e) => setForm((f) => ({ ...f, zip: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Timezone</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={form.timezone}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
            >
              {SETTINGS_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium">Ticket Prefix</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.ticketNumberPrefix}
                onChange={(e) => setForm((f) => ({ ...f, ticketNumberPrefix: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Tax Rate (%)</span>
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.taxRate}
                onChange={(e) => setForm((f) => ({ ...f, taxRate: e.target.value }))}
                placeholder="6"
              />
              <span className="mt-1 block text-xs text-slate-500">
                Enter a whole number, e.g. 6 for 6%.
              </span>
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
              onClick={() => setSlideOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saveMut.isPending}
              className="flex-1 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saveMut.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create Location'}
            </button>
          </div>
          {editing ? (
            <button
              type="button"
              className="w-full rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold text-red-700"
              onClick={() => {
                if (
                  window.confirm(
                    'Are you sure? This will prevent staff from logging in to this location.',
                  )
                ) {
                  deactivateMut.mutate(editing.id);
                }
              }}
            >
              Deactivate Location
            </button>
          ) : null}
        </form>
      </SlideOver>
    </div>
  );
}
